import React, { useState, useEffect, useRef } from "react";
import { ThemeProvider, createTheme } from "@mui/material";
import { useAuthState } from "react-firebase-hooks/auth";
import Layout from "./components/Layout";
import Recorder from "./components/Recorder";
import Settings from "./components/Settings";
import Login from "./components/Login";
import { auth } from "./firebase";
import { useReactMediaRecorder } from "react-media-recorder";
import axios from "axios";

// No longer importing Firestore functions that might be causing issues
// import { fetchUserMessages, saveMessage } from "./utils/conversationService";

const muiTheme = createTheme({
  palette: {
    primary: {
      main: "#7E57C2", // Purple
    },
    secondary: {
      main: "#D1C4E9", // Light purple
    },
  },
});

// Fixed coaching prompt that cannot be changed by the user
const COACHING_PROMPT = `You are a non-directive coaching AI. Your goal is to foster self-discovery and personal growth by asking open-ended questions, reflecting the user's thoughts, and supporting them in identifying their own solutions. You will:

Listen Empathetically: Show genuine curiosity and understanding of the user's perspective.
Ask Open-Ended Questions: Avoid telling the user what to do; instead, prompt them to explore their experiences, feelings, and possibilities.
Facilitate Reflection: Provide space for the user to process their own insights.
Empower the User: Encourage them to identify their own strategies and solutions rather than giving direct advice.
Avoid providing prescriptive instructions or judgments. Instead, focus on creating a safe, supportive environment that helps the user clarify their thinking and move toward their goals on their own terms`;

interface Message {
  text: string;
  isUser: boolean;
  timestamp: number;
}

// Update the saveMessageToLocalStorage and loadMessagesFromLocalStorage functions
const saveMessageToLocalStorage = (message: Message, userId: string) => {
  try {
    // Create a key specific to this user
    const storageKey = `voice_app_messages_${userId}`;
    // Get existing messages for this user
    const existingMessages = JSON.parse(
      localStorage.getItem(storageKey) || "[]"
    );
    // Add new message
    existingMessages.push(message);
    // Save back to localStorage
    localStorage.setItem(storageKey, JSON.stringify(existingMessages));
    console.log(
      `Saved message for user ${userId}. Total messages: ${existingMessages.length}`
    );
    return true;
  } catch (error) {
    console.error("Failed to save message to localStorage:", error);
    return false;
  }
};

// Update the loadMessagesFromLocalStorage function to ensure proper loading
const loadMessagesFromLocalStorage = (userId: string): Message[] => {
  try {
    // Create a key specific to this user
    const storageKey = `voice_app_messages_${userId}`;
    // Get messages for this user
    const messages = JSON.parse(localStorage.getItem(storageKey) || "[]");
    console.log(
      `Loaded ${messages.length} messages for user ${userId} from localStorage`
    );
    return messages;
  } catch (error) {
    console.error("Failed to load messages from localStorage:", error);
    return [];
  }
};

// Create a dedicated function for initializing audio context
const initializeAudioContext = () => {
  try {
    // For Safari and older browsers
    window.AudioContext =
      window.AudioContext || (window as any).webkitAudioContext;
    const audioContext = new AudioContext();

    // iOS Safari requires this
    if (audioContext.state === "suspended") {
      const resumeAudio = () => {
        audioContext.resume();
        console.log("AudioContext started");

        // Remove the event listeners once audio is resumed
        document.removeEventListener("touchstart", resumeAudio);
        document.removeEventListener("touchend", resumeAudio);
        document.removeEventListener("click", resumeAudio);
      };

      document.addEventListener("touchstart", resumeAudio);
      document.addEventListener("touchend", resumeAudio);
      document.addEventListener("click", resumeAudio);
    }

    console.log("Audio context initialized with state:", audioContext.state);
    return audioContext;
  } catch (e) {
    console.error("Audio context initialization failed:", e);
    return null;
  }
};

function App() {
  const [user, loading] = useAuthState(auth);
  const [currentTab, setCurrentTab] = useState(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingTimer, setRecordingTimer] = useState<NodeJS.Timeout | null>(
    null
  );

  // Initialize AudioContext early
  const audioContextRef = useRef<AudioContext | null>(null);

  // Initialize audio context on component mount
  useEffect(() => {
    audioContextRef.current = initializeAudioContext();

    // iOS requires user interaction to enable audio
    const enableAudioOnUserInteraction = () => {
      if (
        audioContextRef.current &&
        audioContextRef.current.state === "suspended"
      ) {
        audioContextRef.current.resume().then(() => {
          console.log("AudioContext resumed by user interaction");
        });
      }
    };

    // Add event listeners for user interaction
    document.addEventListener("touchstart", enableAudioOnUserInteraction);
    document.addEventListener("click", enableAudioOnUserInteraction);

    return () => {
      document.removeEventListener("touchstart", enableAudioOnUserInteraction);
      document.removeEventListener("click", enableAudioOnUserInteraction);
    };
  }, []);

  // Update the useReactMediaRecorder configuration for better mobile compatibility
  const { status, startRecording, stopRecording, mediaBlobUrl, clearBlobUrl } =
    useReactMediaRecorder({
      audio: true,
      // Super simple settings for maximum compatibility on all devices, including iOS
      mediaRecorderOptions: {
        audioBitsPerSecond: 16000, // 16kHz is better for speech
        mimeType: "", // Let browser choose the format
      },
      onStart: () => {
        // Detect mobile
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        console.log(
          `Recording started on ${isMobile ? "mobile" : "desktop"} device`
        );

        if (isMobile) {
          // For mobile, attempt to prevent audio session interruptions
          if (
            audioContextRef.current &&
            audioContextRef.current.state === "suspended"
          ) {
            audioContextRef.current
              .resume()
              .then(() => {
                console.log("AudioContext resumed when recording started");
              })
              .catch((err: Error) => {
                console.error(
                  "Failed to resume AudioContext at recording start:",
                  err
                );
              });
          }
        }
      },
      onStop: async (blobUrl, blob) => {
        // Detect mobile
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

        console.log(
          `Recording stopped on ${
            isMobile ? "mobile" : "desktop"
          } device, blob size:`,
          blob?.size,
          "blob type:",
          blob?.type
        );

        // More stringent checking to ensure blob is valid
        if (!blobUrl || !blob) {
          console.error("Recording failed: No blob or URL available");
          setMessages((prev) => [
            ...prev,
            {
              text: "Recording failed. Please check microphone permissions and try again.",
              isUser: false,
              timestamp: Date.now(),
            },
          ]);
          setIsLoading(false);
          return;
        }

        // Check if recording is too small (likely failed or empty recording)
        const minSizeForMobile = isMobile ? 20 : 100; // Lower threshold for mobile devices
        if (blob.size <= minSizeForMobile) {
          console.error(
            "Invalid recording: blob size too small or recording failed",
            blob
          );

          setMessages((prev) => [
            ...prev,
            {
              text: isMobile
                ? "Recording was too short. Try holding the record button longer, speaking clearly, and waiting for the recording to fully process."
                : "Recording was too short or empty. Please try again in a quiet environment.",
              isUser: false,
              timestamp: Date.now(),
            },
          ]);
          setIsLoading(false);
          return;
        }

        // Valid recording, proceed with transcription
        console.log(
          `Valid recording detected on ${
            isMobile ? "mobile" : "desktop"
          }, proceeding with transcription`
        );

        try {
          // On mobile, add a slight delay to ensure the blob is fully processed
          if (isMobile) {
            console.log("Adding delay for mobile device before transcription");
            await new Promise((resolve) => setTimeout(resolve, 800));
          }

          await handleTranscribe(blobUrl);
        } catch (err) {
          console.error("Error in transcription process:", err);
          setMessages((prev) => [
            ...prev,
            {
              text: `Transcription failed. Please try again. ${
                isIOS
                  ? "On iOS, try recording in a quiet environment and speak clearly into the microphone."
                  : ""
              }`,
              isUser: false,
              timestamp: Date.now(),
            },
          ]);
          setIsLoading(false);
        }
      },
    });

  // Initialize conversation history when user logs in
  useEffect(() => {
    if (user) {
      // Load conversation history from localStorage
      const loadMessages = async () => {
        try {
          setIsLoading(true);
          // Use localStorage directly to load messages for the specific user
          const userMessages = loadMessagesFromLocalStorage(user.uid);
          if (userMessages.length > 0) {
            console.log(
              `Loaded ${userMessages.length} messages for user ${user.uid}`
            );
            setMessages(userMessages);
          }
        } catch (error) {
          console.error("Error loading messages:", error);
        } finally {
          setIsLoading(false);
        }
      };

      loadMessages();
    } else {
      // Clear messages when user logs out
      setMessages([]);
    }
  }, [user]); // Add user as a dependency to reload when user changes

  // Enhance the recording timer logic in useEffect
  useEffect(() => {
    if (status === "recording") {
      // Clear any existing timer first to prevent multiple timers
      if (recordingTimer) {
        clearInterval(recordingTimer);
      }

      // Create a new timer that updates every second
      const timer = setInterval(() => {
        setRecordingTime((prev) => {
          console.log(`Recording time updated: ${prev + 1}s`); // Add logging
          return prev + 1;
        });
      }, 1000);

      setRecordingTimer(timer);
      console.log("Recording timer started");
    } else {
      // Clean up when not recording
      if (recordingTimer) {
        clearInterval(recordingTimer);
        setRecordingTimer(null);
        console.log("Recording timer stopped");
      }

      // Reset timer when stopped
      if (status === "stopped") {
        setRecordingTime(0);
        console.log("Recording time reset to 0");
      }
    }

    // Clean up on unmount
    return () => {
      if (recordingTimer) {
        clearInterval(recordingTimer);
        console.log("Recording timer cleaned up");
      }
    };
  }, [status]); // Remove recordingTimer from dependencies to prevent re-triggering

  // Enhanced function to handle recording start with better iOS compatibility
  const handleStartRecording = () => {
    // Clear previous recording data
    clearBlobUrl();

    // Detect iOS
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    console.log(`Starting recording on ${isIOS ? "iOS" : "non-iOS"} device`);

    // iOS-specific handling
    if (isIOS) {
      // Reset recording timer
      setRecordingTime(0);

      // Set up the recording timer
      const timer = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
      setRecordingTimer(timer);

      // On iOS, we use simpler audio constraints to avoid issues
      console.log("Using iOS-specific recording settings");

      // Ensure AudioContext is active
      if (
        audioContextRef.current &&
        audioContextRef.current.state === "suspended"
      ) {
        audioContextRef.current.resume().catch((e: Error) => {
          console.error("Failed to resume AudioContext for iOS recording:", e);
        });
      }

      // Start recording directly without custom constraints for iOS
      startRecording();

      // Attempt to prevent device sleep during recording
      try {
        if (
          "wakeLock" in navigator &&
          typeof (navigator as any).wakeLock.request === "function"
        ) {
          (navigator as any).wakeLock
            .request("screen")
            .then((wakeLock: any) => {
              console.log("Wake Lock active on iOS");
              (window as any).wakeLockObject = wakeLock;
            })
            .catch((err: Error) => {
              console.error("Wake Lock error on iOS:", err);
            });
        }
      } catch (e) {
        console.error("Failed to set wake lock on iOS:", e);
      }

      return;
    }

    // Non-iOS devices can use better audio settings
    console.log("Starting recording with enhanced audio quality settings...");

    // Try to get microphone access with improved audio settings for non-iOS
    navigator.mediaDevices
      .getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100, // CD quality
          channelCount: 1, // Mono is better for speech recognition
        },
      })
      .then((stream) => {
        // Log the audio tracks to verify settings
        console.log(
          "Audio tracks with enhanced settings:",
          stream.getAudioTracks().map((track) => ({
            label: track.label,
            constraints: track.getConstraints(),
          }))
        );

        // We don't need to keep this stream as the recorder will get its own
        stream.getTracks().forEach((track) => track.stop());

        // Reset recording timer
        setRecordingTime(0);

        // Set up the recording timer
        const timer = setInterval(() => {
          setRecordingTime((prevTime) => prevTime + 1);
        }, 1000);
        setRecordingTimer(timer);

        // Start the actual recording
        startRecording();

        // Attempt to prevent device sleep during recording
        try {
          if (
            "wakeLock" in navigator &&
            typeof (navigator as any).wakeLock.request === "function"
          ) {
            (navigator as any).wakeLock
              .request("screen")
              .then((wakeLock: any) => {
                console.log("Wake Lock active");
                (window as any).wakeLockObject = wakeLock;
              })
              .catch((err: Error) => {
                console.error("Wake Lock error:", err);
              });
          } else {
            console.log("Wake Lock API not supported");
          }
        } catch (e) {
          console.error("Wake Lock error:", e);
        }
      })
      .catch((err) => {
        console.error(
          "Error accessing microphone with enhanced settings:",
          err
        );

        // Fallback to default recording if enhanced settings fail
        console.log("Falling back to default recording settings");

        // Reset recording timer
        setRecordingTime(0);

        // Set up the recording timer
        const timer = setInterval(() => {
          setRecordingTime((prevTime) => prevTime + 1);
        }, 1000);
        setRecordingTimer(timer);

        // Start recording with default settings
        startRecording();
      });
  };

  // Add a function to handle stopping recording and releasing resources
  const handleStopRecording = () => {
    stopRecording();

    // Release wake lock if active
    try {
      const wakeLockObj = (window as any).wakeLockObject;
      if (wakeLockObj && typeof wakeLockObj.release === "function") {
        wakeLockObj.release();
        console.log("Wake Lock released");
        (window as any).wakeLockObject = null;
      }
    } catch (err) {
      console.error("Error releasing Wake Lock:", err);
    }
  };

  // Update handleRecordingToggle with better mobile diagnostics
  const handleRecordingToggle = () => {
    // Check if the API key exists before recording
    if (!process.env.REACT_APP_OPENAI_API_KEY) {
      console.error("OpenAI API key not found");
      setCurrentTab(1); // Switch to settings
      setMessages((prev) => [
        ...prev,
        {
          text: "Please set your OpenAI API key in Settings before recording.",
          isUser: false,
          timestamp: Date.now(),
        },
      ]);
      return;
    }

    // Log device information for debugging
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    console.log("Device info:", {
      userAgent: navigator.userAgent,
      isMobile,
      hasMediaDevices: !!navigator.mediaDevices,
    });

    // Ensure audio context is active, especially for iOS
    if (audioContextRef.current) {
      if (audioContextRef.current.state === "suspended") {
        audioContextRef.current
          .resume()
          .then(() => {
            console.log("AudioContext resumed before recording");
          })
          .catch((err: Error) => {
            console.error("Failed to resume AudioContext:", err);
          });
      }
    }

    if (status !== "recording") {
      // Try to get microphone permissions explicitly first on mobile to show proper UI
      if (isMobile && navigator.mediaDevices) {
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then((stream) => {
            console.log("Microphone permission granted");
            // Stop the stream immediately, we just needed the permission
            stream.getTracks().forEach((track) => track.stop());
            handleStartRecording();
          })
          .catch((err: Error) => {
            console.error("Microphone permission error:", err);
            let errorMsg =
              "Could not access microphone. Please check permissions.";

            // Provide more specific error messages based on the error
            if (err.name === "NotAllowedError") {
              errorMsg =
                "Microphone access was denied. Please allow microphone access in your browser settings.";
            } else if (err.name === "NotFoundError") {
              errorMsg =
                "No microphone found. Please connect a microphone and try again.";
            } else if (err.name === "NotReadableError") {
              errorMsg =
                "Microphone is in use by another application. Please close other apps using the microphone.";
            }

            setMessages((prev) => [
              ...prev,
              {
                text: errorMsg,
                isUser: false,
                timestamp: Date.now(),
              },
            ]);
          });
      } else {
        // Desktop or fallback
        handleStartRecording();
      }
    } else {
      handleStopRecording();
    }
  };

  // Update the handleTranscribe function to handle mobile audio formats better
  const handleTranscribe = async (blobUrl: string) => {
    try {
      setIsLoading(true);

      // Detect mobile device types
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      const isAndroid = /Android/i.test(navigator.userAgent);

      console.log(
        `Transcribing on ${
          isIOS ? "iOS" : isAndroid ? "Android" : "desktop"
        } device`
      );

      // Get the blob from the blobUrl
      const audioBlob = await fetch(blobUrl).then((r) => r.blob());
      console.log("Audio blob details:", {
        size: audioBlob.size,
        type: audioBlob.type,
        isIOS,
        isAndroid,
        userAgent: navigator.userAgent,
      });

      // Create a proper audio blob for transcription
      let transcriptionBlob = audioBlob;
      let fileExtension = "mp3"; // Default extension

      // Mobile device handling
      if (isMobile) {
        if (isIOS) {
          console.log("Using iOS-specific audio handling");

          // For iOS Safari, we'll try multiple approaches to ensure compatibility
          try {
            // Try forcing to m4a first (most compatible with iOS)
            const arrayBuffer = await audioBlob.arrayBuffer();

            console.log("Array buffer details:", {
              byteLength: arrayBuffer.byteLength,
              isValid: arrayBuffer.byteLength > 0,
            });

            // For iOS, always try m4a first
            transcriptionBlob = new Blob([arrayBuffer], { type: "audio/m4a" });
            fileExtension = "m4a";

            console.log("Created m4a blob:", {
              size: transcriptionBlob.size,
              type: transcriptionBlob.type,
            });
          } catch (e) {
            console.error("Failed to create m4a blob:", e);

            // If that fails, try a different approach - use the original blob with correct extension
            if (audioBlob.type.includes("audio/")) {
              if (
                audioBlob.type.includes("mp4") ||
                audioBlob.type.includes("m4a")
              ) {
                fileExtension = "m4a";
              } else if (audioBlob.type.includes("mp3")) {
                fileExtension = "mp3";
              } else {
                // Default to mp3 for other audio types
                fileExtension = "mp3";
              }
            } else {
              // For unknown types on iOS, default to m4a
              fileExtension = "m4a";
            }
          }
        } else if (isAndroid) {
          console.log("Using Android-specific audio handling");

          // For Android, WebM is often most reliable
          if (audioBlob.type.includes("webm")) {
            fileExtension = "webm";
          } else if (audioBlob.type.includes("mp3")) {
            fileExtension = "mp3";
          } else {
            // Try to create a WebM blob as fallback for Android
            try {
              transcriptionBlob = new Blob([await audioBlob.arrayBuffer()], {
                type: "audio/webm",
              });
              fileExtension = "webm";
            } catch (e) {
              console.error("Failed to create webm blob:", e);
              // Use mp3 as final fallback
              fileExtension = "mp3";
            }
          }
        }
      } else {
        // Non-mobile handling (desktop browsers)
        if (audioBlob.type.includes("audio/")) {
          if (audioBlob.type.includes("webm")) {
            fileExtension = "webm";
          } else if (
            audioBlob.type.includes("mp4") ||
            audioBlob.type.includes("m4a")
          ) {
            fileExtension = "m4a";
          } else if (audioBlob.type.includes("mp3")) {
            fileExtension = "mp3";
          } else if (audioBlob.type.includes("wav")) {
            fileExtension = "wav";
          } else if (audioBlob.type.includes("ogg")) {
            fileExtension = "ogg";
          }
        } else {
          // If the blob type is not what we expect, try to create a new blob with proper type
          console.log(
            "Audio blob has incorrect mime type, attempting to correct"
          );
          try {
            transcriptionBlob = new Blob([await audioBlob.arrayBuffer()], {
              type: "audio/mp3",
            });
            fileExtension = "mp3";
          } catch (e) {
            console.error("Failed to create new blob:", e);
            // Continue with original blob and use mp3 as a fallback
          }
        }
      }

      // Ensure we have a valid file name with the correct extension
      const fileName = `recording.${fileExtension}`;
      console.log(
        `Using filename: ${fileName} with extension ${fileExtension} for transcription`
      );
      console.log(
        `Final blob MIME type: ${transcriptionBlob.type}, size: ${transcriptionBlob.size}`
      );

      // Create FormData and append the blob
      const formData = new FormData();
      formData.append("file", transcriptionBlob, fileName);

      // Use the Whisper model for transcription
      formData.append("model", "whisper-1");

      // Always add language for all devices
      formData.append("language", "en");

      // For desktop and Android, we can use more options
      // For iOS, keep it minimal for maximum compatibility
      if (!isIOS) {
        formData.append("prompt", "The following is a conversation.");
        if (!isMobile) {
          // Only add these on desktop for maximum compatibility
          formData.append("temperature", "0");
          formData.append("response_format", "json");
        }
      }

      // Log the key status (securely)
      console.log(
        "Using API key:",
        process.env.REACT_APP_OPENAI_API_KEY ? "Key exists" : "No key found"
      );

      // Log the final form data for debugging (without iteration)
      console.log("FormData contents:", {
        model: "whisper-1",
        language: "en",
        hasFile: !!transcriptionBlob,
        fileName,
        fileType: transcriptionBlob.type,
        fileSize: transcriptionBlob.size,
        hasPrompt: !isIOS,
        hasResponseFormat: !isMobile,
      });

      // Extra delay for mobile devices to ensure everything is ready
      if (isMobile) {
        console.log(`Adding ${isIOS ? "iOS" : "Android"} pre-request delay`);
        await new Promise((resolve) => setTimeout(resolve, isIOS ? 500 : 300));
      }

      console.log("Sending transcription request to OpenAI...");
      const response = await axios.post(
        "https://api.openai.com/v1/audio/transcriptions",
        formData,
        {
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 30000, // Longer timeout for mobile devices
        }
      );

      console.log(
        "OpenAI API response received, type:",
        typeof response.data,
        "IsString:",
        typeof response.data === "string",
        "HasTextProperty:",
        !!response.data.text
      );

      // Handle the response based on format
      let transcription = "";
      if (response.data) {
        // Try multiple methods to extract the transcription text
        if (typeof response.data === "string") {
          transcription = response.data;
          console.log("Extracted string response:", transcription);
        } else if (response.data.text) {
          transcription = response.data.text;
          console.log("Extracted text property:", transcription);
        } else {
          // For unusual response formats, try to extract the text
          console.log("Unusual response format, trying to extract text");
          try {
            const responseStr = JSON.stringify(response.data);
            console.log("Full response as string:", responseStr);

            // Try to find any text content in the response
            if (responseStr.includes('"text"')) {
              const match = responseStr.match(/"text"\s*:\s*"([^"]+)"/);
              if (match && match[1]) {
                transcription = match[1];
                console.log("Extracted text from JSON string:", transcription);
              }
            }

            // If still no transcription, look for any quoted string that might be the text
            if (!transcription && typeof response.data === "object") {
              for (const key in response.data) {
                if (
                  typeof response.data[key] === "string" &&
                  response.data[key].length > 5
                ) {
                  transcription = response.data[key];
                  console.log(
                    `Found potential transcription in key "${key}":`,
                    transcription
                  );
                  break;
                }
              }
            }
          } catch (e) {
            console.error("Error processing response:", e);
          }
        }

        console.log("Final transcription result:", transcription);

        // If transcription is empty or too short on mobile, provide specific guidance
        if (
          !transcription ||
          (isMobile && transcription.trim().split(/\s+/).length < 2)
        ) {
          console.error(
            "Empty or very short transcription result. Full response:",
            typeof response.data === "object"
              ? JSON.stringify(response.data)
              : response.data
          );

          const errorMessage = {
            text: isMobile
              ? isIOS
                ? "Could not transcribe recording on iOS. Please try again, speaking slowly and clearly, and holding the phone closer to your mouth."
                : "Could not transcribe recording on Android. Please try again in a quieter environment."
              : "Could not transcribe audio. Please try again in a quieter environment.",
            isUser: false,
            timestamp: Date.now(),
          };

          setMessages((prev) => [...prev, errorMessage]);

          if (user) {
            saveMessageToLocalStorage(errorMessage, user.uid);
          }

          setIsLoading(false);
          return;
        }

        // Process the transcription - clean up text if needed
        transcription = transcription.trim();

        // Fix common transcription issues
        transcription = transcription
          .replace(/(\w)\.(\w)/g, "$1. $2") // Add space after period between words
          .replace(/\s+/g, " ") // Remove multiple spaces
          .replace(/^\s+|\s+$/g, ""); // Trim whitespace

        // Capitalize first letter
        if (transcription.length > 0) {
          transcription =
            transcription.charAt(0).toUpperCase() + transcription.slice(1);
        }

        const userMessage = {
          text: transcription,
          isUser: true,
          timestamp: Date.now(),
        };

        // Add the user message to the conversation
        setMessages((prev) => [...prev, userMessage]);

        // Save to localStorage
        if (user) {
          saveMessageToLocalStorage(userMessage, user.uid);
        }

        // Process with ChatGPT including full history
        await handleChatGPTResponse(transcription);
      } else {
        console.error("Empty response from API");
        const errorMessage = {
          text: isMobile
            ? "No response from the transcription service. Please check your internet connection and try again."
            : "Could not transcribe audio. No response from the transcription service.",
          isUser: false,
          timestamp: Date.now(),
        };

        setMessages((prev) => [...prev, errorMessage]);

        if (user) {
          saveMessageToLocalStorage(errorMessage, user.uid);
        }
      }
    } catch (error: any) {
      console.error("Error transcribing audio:", error);

      // Detect device type for better error messages
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

      // Log detailed error information
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
        console.error("Response headers:", error.response.headers);
      } else if (error.request) {
        console.error("No response received:", error.request);
      } else {
        console.error("Error details:", error.message);
      }

      // Check for specific error types and provide appropriate messages
      let errorMsg = "Please try again.";

      if (error.response?.data?.error?.message) {
        const errorMessage = error.response.data.error.message;
        console.error("API error message:", errorMessage);

        if (errorMessage.includes("Invalid file format")) {
          errorMsg = isMobile
            ? `Invalid audio format. ${
                isIOS ? "iOS sometimes produces incompatible recordings." : ""
              } Try recording again in a quieter environment.`
            : "Invalid file format. Please try recording again with a supported format.";
        } else if (errorMessage.includes("Maximum file size")) {
          errorMsg =
            "Recording was too long. Please keep recordings under 1 minute.";
        } else if (errorMessage.includes("minimum file size")) {
          errorMsg =
            "Recording was too short. Please speak for at least 1-2 seconds.";
        } else {
          // Use the API's error message if it's descriptive
          errorMsg = errorMessage;
        }
      } else if (error.message?.includes("timeout")) {
        errorMsg =
          "Request timed out. Please check your internet connection and try again.";
      } else if (error.message?.includes("Network Error")) {
        errorMsg =
          "Network error. Please check your internet connection and try again.";
      }

      const errorMessage = {
        text: `Error transcribing audio: ${errorMsg}`,
        isUser: false,
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, errorMessage]);

      if (user) {
        saveMessageToLocalStorage(errorMessage, user.uid);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChatGPTResponse = async (transcription: string) => {
    try {
      setIsLoading(true);

      // Format all previous messages for the API
      const conversationHistory = messages.map((msg) => ({
        role: msg.isUser ? "user" : "assistant",
        content: msg.text,
      }));

      // Add the latest user message
      conversationHistory.push({
        role: "user",
        content: transcription,
      });

      // Prepare the API request with full conversation history
      const apiMessages = [
        {
          role: "system",
          content: COACHING_PROMPT, // The coaching instructions
        },
        ...conversationHistory, // Include the entire conversation history
      ];

      console.log("Sending full conversation context to AI:", apiMessages);

      const response = await axios.post(
        "https://api.openai.com/v1/chat/completions",
        {
          model: "gpt-4",
          messages: apiMessages,
        },
        {
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.choices?.[0]?.message?.content) {
        const aiResponse = response.data.choices[0].message.content;
        const aiMessage = {
          text: aiResponse,
          isUser: false,
          timestamp: Date.now(),
        };

        setMessages((prev) => [...prev, aiMessage]);

        // Save to localStorage instead of Firestore
        if (user) {
          saveMessageToLocalStorage(aiMessage, user.uid);
        }
      }
    } catch (error) {
      console.error("Error getting AI response:", error);
      const errorMessage = {
        text: "Error getting response from AI. Please try again.",
        isUser: false,
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, errorMessage]);

      // Save to localStorage instead of Firestore
      if (user) {
        saveMessageToLocalStorage(errorMessage, user.uid);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearConversation = async () => {
    if (user) {
      // Clear messages from localStorage
      localStorage.setItem(
        `voice_app_messages_${user.uid}`,
        JSON.stringify([])
      );
    }
    setMessages([]);
  };

  if (loading) {
    return null; // Or a loading spinner
  }

  if (!user) {
    return (
      <ThemeProvider theme={muiTheme}>
        <Login />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <Layout currentTab={currentTab} onTabChange={setCurrentTab}>
        {currentTab === 0 ? (
          <Recorder
            status={status}
            isLoading={isLoading}
            messages={messages}
            onRecordingToggle={handleRecordingToggle}
            recordingTime={recordingTime}
            onClearConversation={handleClearConversation}
          />
        ) : (
          <Settings />
        )}
      </Layout>
    </ThemeProvider>
  );
}

export default App;
